import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

export const useWrappedAnalytics = (year: number = 2025) => {
  const { userId } = useAuth();
  const pendingUpdates = useRef<{
    slideViews: Record<string, number>;
    shareClicks: Record<string, number>;
  }>({ slideViews: {}, shareClicks: {} });
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const flushUpdates = useCallback(async () => {
    if (!userId) return;

    const updates = { ...pendingUpdates.current };
    pendingUpdates.current = { slideViews: {}, shareClicks: {} };

    const hasSlideViews = Object.keys(updates.slideViews).length > 0;
    const hasShareClicks = Object.keys(updates.shareClicks).length > 0;

    if (!hasSlideViews && !hasShareClicks) return;

    try {
      // Fetch current stats
      const { data: currentStats } = await supabase
        .from("wrapped_stats")
        .select("slide_views, share_clicks, total_views, total_shares")
        .eq("user_id", userId)
        .eq("year", year)
        .single();

      if (!currentStats) return;

      const currentSlideViews = (currentStats.slide_views as Record<string, number>) || {};
      const currentShareClicks = (currentStats.share_clicks as Record<string, number>) || {};

      // Merge updates
      const newSlideViews = { ...currentSlideViews };
      const newShareClicks = { ...currentShareClicks };
      let viewsToAdd = 0;
      let sharesToAdd = 0;

      for (const [slide, count] of Object.entries(updates.slideViews)) {
        newSlideViews[slide] = (newSlideViews[slide] || 0) + count;
        viewsToAdd += count;
      }

      for (const [slide, count] of Object.entries(updates.shareClicks)) {
        newShareClicks[slide] = (newShareClicks[slide] || 0) + count;
        sharesToAdd += count;
      }

      // Update the stats
      await supabase
        .from("wrapped_stats")
        .update({
          slide_views: newSlideViews,
          share_clicks: newShareClicks,
          total_views: (currentStats.total_views || 0) + viewsToAdd,
          total_shares: (currentStats.total_shares || 0) + sharesToAdd,
          last_viewed_at: new Date().toISOString(),
        })
        .eq("user_id", userId)
        .eq("year", year);
    } catch (error) {
      console.error("Failed to update wrapped analytics:", error);
    }
  }, [userId, year]);

  const scheduleFlush = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(flushUpdates, 1000);
  }, [flushUpdates]);

  const trackSlideView = useCallback((slideId: string) => {
    if (!userId) return;
    pendingUpdates.current.slideViews[slideId] = 
      (pendingUpdates.current.slideViews[slideId] || 0) + 1;
    scheduleFlush();
  }, [userId, scheduleFlush]);

  const trackShareClick = useCallback((slideId: string) => {
    if (!userId) return;
    pendingUpdates.current.shareClicks[slideId] = 
      (pendingUpdates.current.shareClicks[slideId] || 0) + 1;
    scheduleFlush();
  }, [userId, scheduleFlush]);

  return { trackSlideView, trackShareClick };
};
